"use client";

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CheckCircle2, Loader2, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PaymentMethodSelector } from "@/components/payment-methods/payment-method-selector";
import { useSDK, useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { SDK } from "@/@sdk";
import { selectIntegrationConfig_Stripe } from "@/store/modules/workspace/workspace.selectors";
import { setOrganization } from "@/store/modules/workspace/workspace.actions";
import { iOrganization } from "@/@sdk/user-management/user-management.models";
import {
  iBookingConfirmationTemplate,
  iBookingPaymentReminderTemplate,
  iBrand,
} from "@/@sdk/brands/brand-model";
import { useBrands } from "@/hooks/use-brands";
import { defaultPaymentReminderTemplate } from "@/data/default-booking-payment-reminder-template";
import { defaultBookingConfirmationTemplate } from "@/data/default-booking-confirmation-template";
import { IntegrationCard } from "./integration-card";
import { STRIPE_INTEGRATION_INFO } from "./integration-info-content";
import { IntegrationLogo } from "./integration-logo";

function mergePaymentReminderStripeFlag(
  template: iBookingPaymentReminderTemplate | undefined,
  includeStripePayButton: boolean
): iBookingPaymentReminderTemplate {
  const base = template ?? defaultPaymentReminderTemplate;
  return {
    ...base,
    includeStripePayButton,
  };
}

function mergeBookingConfirmationStripeFlag(
  template: iBookingConfirmationTemplate | undefined,
  includeStripePayButton: boolean
): iBookingConfirmationTemplate {
  const base = template ?? defaultBookingConfirmationTemplate;
  return {
    ...base,
    includeStripePayButton,
  };
}

type BrandStripeToggleKey =
  | `reminder:${string}`
  | `confirmation:${string}`
  | `paymentMethod:${string}`;

function brandStripePaymentMethodId(brand: iBrand): string {
  return brand.stripeConfig?.customerPaymentMethodId?.trim() ?? "";
}

function isBrandStripeReady(
  integrationConfig: { auth?: { apiKey?: string } } | null | undefined,
  brand: iBrand
): boolean {
  return Boolean(
    integrationConfig?.auth?.apiKey && brandStripePaymentMethodId(brand)
  );
}

const stripeFormSchema = z.object({
  apiKey: z.string().min(1, "Please enter an API Key"),
});

type StripeFormValues = z.infer<typeof stripeFormSchema>;

export function StripeSection() {
  const dispatch = useDispatch();
  const integrationConfig = useSelector(selectIntegrationConfig_Stripe);
  const connected = Boolean(integrationConfig);
  const { activeBrands, retry: retryBrands } = useBrands();
  const [savingBrandToggle, setSavingBrandToggle] =
    useState<BrandStripeToggleKey | null>(null);
  const [brandPaymentMethodDrafts, setBrandPaymentMethodDrafts] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const brand of activeBrands) {
      if (!brand.id) continue;
      next[brand.id] = brandStripePaymentMethodId(brand);
    }
    setBrandPaymentMethodDrafts(next);
  }, [activeBrands]);

  const { doAction: completeIntegration, isProcessing: isAdding } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => (apiKey: string) =>
          SDK.externalAccounts.completeIntegration({
            type: "STRIPE",
            data: { apiKey },
          }),
        successMessage: "Stripe connected successfully",
        failureMessage: "Failed to connect Stripe",
        onSuccess: (res: iOrganization) => {
          dispatch(setOrganization(res));
        },
        actionDependencies: [],
      }),
      [dispatch]
    );

  const { doAction: removeIntegration, isProcessing: isRemoving } =
    useSDKActionWithDeps(
      () => ({
        action: (SDK) => () =>
          SDK.externalAccounts.removeIntegration({ type: "STRIPE" }),
        successMessage: "Stripe integration removed",
        failureMessage: "Failed to remove Stripe integration",
        onSuccess: (res: iOrganization) => {
          dispatch(setOrganization(res));
        },
        actionDependencies: [],
      }),
      [dispatch]
    );

  const handleBrandPaymentMethodSave = useCallback(
    async (brand: iBrand) => {
      if (!brand.id) return;
      const toggleKey: BrandStripeToggleKey = `paymentMethod:${brand.id}`;
      setSavingBrandToggle(toggleKey);
      try {
        const customerPaymentMethodId =
          brandPaymentMethodDrafts[brand.id]?.trim() || undefined;
        await SDK.brands.editById(brand.id, {
          stripeConfig: {
            ...(brand.stripeConfig ?? {}),
            customerPaymentMethodId,
          },
        });
        retryBrands();
        toast.success(`Stripe payment method saved for ${brand.name}`);
      } catch {
        toast.error("Failed to save Stripe payment method");
      } finally {
        setSavingBrandToggle(null);
      }
    },
    [brandPaymentMethodDrafts, retryBrands]
  );

  const handleBrandReminderPayButtonToggle = useCallback(
    async (brand: iBrand, enabled: boolean) => {
      if (!brand.id) return;
      const toggleKey: BrandStripeToggleKey = `reminder:${brand.id}`;
      setSavingBrandToggle(toggleKey);
      try {
        await SDK.brands.editById(brand.id, {
          bookingPaymentReminderTemplate: mergePaymentReminderStripeFlag(
            brand.bookingPaymentReminderTemplate,
            enabled
          ),
        });
        retryBrands();
        toast.success(
          enabled
            ? `Payment reminder pay button enabled for ${brand.name}`
            : `Payment reminder pay button disabled for ${brand.name}`
        );
      } catch {
        toast.error("Failed to update payment reminder pay button setting");
      } finally {
        setSavingBrandToggle(null);
      }
    },
    [retryBrands]
  );

  const handleBrandConfirmationPayButtonToggle = useCallback(
    async (brand: iBrand, enabled: boolean) => {
      if (!brand.id) return;
      const toggleKey: BrandStripeToggleKey = `confirmation:${brand.id}`;
      setSavingBrandToggle(toggleKey);
      try {
        await SDK.brands.editById(brand.id, {
          bookingConfirmationTemplate: mergeBookingConfirmationStripeFlag(
            brand.bookingConfirmationTemplate,
            enabled
          ),
        });
        retryBrands();
        toast.success(
          enabled
            ? `Booking confirmation pay button enabled for ${brand.name}`
            : `Booking confirmation pay button disabled for ${brand.name}`
        );
      } catch {
        toast.error("Failed to update booking confirmation pay button setting");
      } finally {
        setSavingBrandToggle(null);
      }
    },
    [retryBrands]
  );

  const form = useForm<StripeFormValues>({
    resolver: zodResolver(stripeFormSchema),
    defaultValues: { apiKey: "" },
  });

  const onSubmit = useCallback(
    async (values: StripeFormValues) => {
      try {
        await completeIntegration(values.apiKey);
        form.reset();
      } catch {
        // Error handled by useSDKActionWithDeps
      }
    },
    [completeIntegration, form]
  );

  const {
    isLoading: isTestingIntegration,
    error: hasTestError,
  } = useSDK(
    (SDK) => SDK.stripe.searchCustomers({ name: "test" }),
    [integrationConfig],
    !connected
  );

  return (
    <IntegrationCard
      connected={connected}
      integrationInfo={STRIPE_INTEGRATION_INFO}
      icon={
        <IntegrationLogo
          src="/assets/integrations/stripe-logo.png"
          alt="Stripe"
        />
      }
      title="Stripe"
      description="Accept quote deposits and booking payments via Stripe Checkout."
    >
      <div className="rounded-lg border border-muted bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">Restricted API key permissions</p>
        <p className="mt-1">
          Create a restricted key in your Stripe Dashboard with these permissions:
        </p>
        <ul className="mt-1 list-inside list-disc">
          <li>Customers — Read</li>
          <li>Checkout Sessions — Write</li>
          <li>Webhook Endpoints — Write</li>
        </ul>
      </div>

      {!connected ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Secret API Key</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="rk_live_... or rk_test_..."
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={isAdding}>
                {isAdding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  "Connect Stripe"
                )}
              </Button>
            </div>
          </form>
        </Form>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeIntegration()}
                    disabled={isRemoving}
                  >
                    {isRemoving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                        Disconnect
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove Stripe integration</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {isTestingIntegration ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Checking connection...</span>
            </div>
          ) : hasTestError ? (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                Stripe connection check failed. Reconnect with a valid API key.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Stripe is connected
                {integrationConfig?.isPaymentDetectionActivated
                  ? " and payment webhooks are active"
                  : ""}
                .
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-3 rounded-lg border p-4">
            <div className="space-y-1">
              <Label>Customer payment methods</Label>
              <p className="text-xs text-muted-foreground">
                Select a customer payment method per brand. Stripe receipts are
                recorded against this method — use one that matches the
                brand&apos;s home currency. Enable PDQ charges on the linked
                method to record Stripe processing fees automatically on each
                payment.
              </p>
            </div>

            {activeBrands.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active brands in this workspace.
              </p>
            ) : (
              <div className="space-y-3">
                {activeBrands.map((brand) => {
                  const brandId = brand.id!;
                  const toggleKey: BrandStripeToggleKey = `paymentMethod:${brandId}`;
                  const isSaving = savingBrandToggle === toggleKey;
                  const draftValue = brandPaymentMethodDrafts[brandId] ?? "";
                  const savedValue = brandStripePaymentMethodId(brand);
                  const isDirty = draftValue !== savedValue;

                  return (
                    <div
                      key={toggleKey}
                      className="space-y-2 rounded-md border px-3 py-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {brand.name}
                          </p>
                          {brand.homeCurrency ? (
                            <p className="text-xs text-muted-foreground">
                              Home currency: {brand.homeCurrency}
                            </p>
                          ) : null}
                        </div>
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                        ) : null}
                      </div>
                      <PaymentMethodSelector
                        id={`stripe-brand-payment-method-${brandId}`}
                        variant="customer"
                        value={draftValue}
                        onValueChange={(value) =>
                          setBrandPaymentMethodDrafts((prev) => ({
                            ...prev,
                            [brandId]: value,
                          }))
                        }
                        label=""
                        hideLabel
                        currencyFilter={brand.homeCurrency}
                        brandIdFilter={brandId}
                        emptyHint={
                          brand.homeCurrency
                            ? `No customer payment methods for ${brand.homeCurrency}`
                            : undefined
                        }
                      />
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isSaving || !isDirty}
                          onClick={() => void handleBrandPaymentMethodSave(brand)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-lg border p-4">
            <div className="space-y-1">
              <Label>Payment reminder pay buttons</Label>
              <p className="text-xs text-muted-foreground">
                Add a &quot;Pay now&quot; button to payment reminder emails for
                each brand. You can also configure this under Brand settings →
                Payment Reminder Emails.
              </p>
            </div>

            {!activeBrands.some((brand) =>
              isBrandStripeReady(integrationConfig, brand)
            ) ? (
              <Alert>
                <AlertDescription>
                  Configure a customer payment method for each brand below
                  before pay buttons can be sent in reminder emails. You can
                  still enable the setting per brand in advance.
                </AlertDescription>
              </Alert>
            ) : null}

            {activeBrands.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active brands in this workspace.
              </p>
            ) : (
              <div className="space-y-2">
                {activeBrands.map((brand) => {
                  const brandId = brand.id!;
                  const toggleKey: BrandStripeToggleKey = `reminder:${brandId}`;
                  const isSaving = savingBrandToggle === toggleKey;
                  const enabled =
                    brand.bookingPaymentReminderTemplate
                      ?.includeStripePayButton === true;

                  return (
                    <div
                      key={toggleKey}
                      className="flex items-center justify-between gap-4 rounded-md border px-3 py-2.5"
                    >
                      <div className="min-w-0 space-y-0.5">
                        <p className="text-sm font-medium truncate">
                          {brand.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Payment reminder emails
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : null}
                        <Switch
                          id={`stripe-reminder-pay-${brandId}`}
                          checked={enabled}
                          disabled={isSaving}
                          onCheckedChange={(checked) =>
                            void handleBrandReminderPayButtonToggle(
                              brand,
                              checked
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-lg border p-4">
            <div className="space-y-1">
              <Label>Booking confirmation pay buttons</Label>
              <p className="text-xs text-muted-foreground">
                Add a &quot;Pay now&quot; button in the How to Pay section of
                booking confirmation emails. Configure per brand under Brand
                settings → Booking Confirmation.
              </p>
            </div>

            {!activeBrands.some((brand) =>
              isBrandStripeReady(integrationConfig, brand)
            ) ? (
              <Alert>
                <AlertDescription>
                  Configure a customer payment method for each brand below
                  before pay buttons can be sent in confirmation emails. You can
                  still enable the setting per brand in advance.
                </AlertDescription>
              </Alert>
            ) : null}

            {activeBrands.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active brands in this workspace.
              </p>
            ) : (
              <div className="space-y-2">
                {activeBrands.map((brand) => {
                  const brandId = brand.id!;
                  const toggleKey: BrandStripeToggleKey = `confirmation:${brandId}`;
                  const isSaving = savingBrandToggle === toggleKey;
                  const enabled =
                    brand.bookingConfirmationTemplate?.includeStripePayButton ===
                    true;

                  return (
                    <div
                      key={toggleKey}
                      className="flex items-center justify-between gap-4 rounded-md border px-3 py-2.5"
                    >
                      <div className="min-w-0 space-y-0.5">
                        <p className="text-sm font-medium truncate">
                          {brand.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Booking confirmation emails
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : null}
                        <Switch
                          id={`stripe-confirmation-pay-${brandId}`}
                          checked={enabled}
                          disabled={isSaving}
                          onCheckedChange={(checked) =>
                            void handleBrandConfirmationPayButtonToggle(
                              brand,
                              checked
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </IntegrationCard>
  );
}
